// AI orchestrator. Triggered on visitor:chat:message → if the matching
// Conversation is owned by AI, derives transcript from the Conversation
// store (single source of truth) and emits the assistant reply.
//
// Phase 5 moves this into the backend; in the mockup it runs in the agent
// app and proxies through the configured LLM provider.

import {
  chat,
  DEFAULT_MODELS,
  type ChatTransportConfig,
  type ChatTurn,
  type Provider,
  type SessionId,
  type VisitorId,
  VISITOR_CHAT_PROMPT,
  getBus,
} from '@prakash-software/shared';
import { useAgentStore } from '../stores/agent';
import { newMessageId, useConversationsStore } from '../stores/conversations';
import { useNotificationsStore } from '../stores/notifications';

const isLoggedIn = () => useAgentStore.getState().current !== null;

const TRANSCRIPT_TAIL = 40;
const announced = new Set<SessionId>();

let installed = false;

function transportFromEnv(): ChatTransportConfig {
  const env = import.meta.env as Record<string, string | undefined>;
  const out: ChatTransportConfig = {};
  if (env.VITE_ANTHROPIC_API_KEY) {
    out.anthropic = {
      apiKey: env.VITE_ANTHROPIC_API_KEY,
      baseUrl: env.VITE_ANTHROPIC_BASE_URL ?? '/api/anthropic',
    };
  }
  if (env.VITE_OPENAI_API_KEY) {
    out.openai = {
      apiKey: env.VITE_OPENAI_API_KEY,
      baseUrl: env.VITE_OPENAI_BASE_URL ?? '/api/openai',
    };
  }
  if (env.VITE_PRAKASH_SOFTWARE_API_KEY) {
    out.openline = {
      apiKey: env.VITE_PRAKASH_SOFTWARE_API_KEY,
      baseUrl: env.VITE_PRAKASH_SOFTWARE_BASE_URL ?? '/api/openline',
    };
  }
  return out;
}

// The per-agent provider picker moved to Admin → System (system-wide defaults).
// The agent-side browser tools below (auto-reply scaffolding + the summary
// button) just use the first provider that has a browser key configured, with
// that provider's default model. The 'custom' provider is server-side only.
function pickBrowserProvider(
  transport: ChatTransportConfig,
): { provider: Provider; model: string } | null {
  for (const p of ['openline', 'anthropic', 'openai'] as const) {
    if (transport[p]) return { provider: p, model: DEFAULT_MODELS[p] };
  }
  return null;
}

export function installAIOrchestrator(): void {
  if (installed) return;
  installed = true;
  const bus = getBus();

  bus.on('visitor:chat:message', (event) => {
    // Visitor auto-routing to AI is owned by the server orchestrator now (per
    // site chat_routing); this browser path is legacy mockup scaffolding and is
    // not installed by main.tsx.
    const sessionId = event.sessionId;
    const visitorId = event.message.authorId as VisitorId | undefined;
    if (!visitorId) return;

    // The conversation must exist (bus-bridge.ts ensures this on the same
    // event) and must currently be owned by the AI.
    const conv = useConversationsStore.getState().byId.get(sessionId);
    if (!conv) return;
    if (conv.ownedBy !== 'ai') return;

    // Announce takeover the first time we touch this session.
    if (!announced.has(sessionId)) {
      announced.add(sessionId);
      bus.emit({
        type: 'ai:takeover',
        sessionId,
        visitorId,
        reason: 'no-agents-online',
        at: Date.now(),
      });
    }

    void replyForSession(sessionId, visitorId);
  });

  // If a human takes over later, clear the "announced" flag so a future
  // re-handoff to AI can announce again.
  useConversationsStore.subscribe((state, prev) => {
    for (const [sid, conv] of state.byId) {
      const prior = prev.byId.get(sid);
      if (prior && prior.ownedBy !== 'agent' && conv.ownedBy === 'agent') {
        announced.delete(sid);
      }
    }
  });
}

async function replyForSession(sessionId: SessionId, visitorId: VisitorId): Promise<void> {
  // Re-read the conversation so we get the freshest transcript (including the
  // visitor's just-appended message).
  const conv = useConversationsStore.getState().byId.get(sessionId);
  if (!conv) return;
  if (conv.ownedBy !== 'ai') return;

  // Include both AI and human-agent turns as 'assistant' — from the
  // visitor's perspective both played the "support" role. This means a
  // hand-off back to AI still carries the human agent's earlier context.
  const turns: ChatTurn[] = conv.messages
    .filter((m) => m.author === 'visitor' || m.author === 'ai' || m.author === 'agent')
    .slice(-TRANSCRIPT_TAIL)
    .map((m) => ({
      role: m.author === 'visitor' ? 'user' : 'assistant',
      content: m.text,
    }));
  // Skip if the last message is already assistant — shouldn't happen, but cheap guard.
  if (turns.length === 0 || turns[turns.length - 1]?.role !== 'user') return;

  const transport = transportFromEnv();
  const picked = pickBrowserProvider(transport);
  if (!picked) {
    if (isLoggedIn()) {
      useNotificationsStore.getState().push({
        kind: 'system',
        title: 'No AI provider configured',
        body: 'Add a VITE_*_API_KEY in .env.local and restart the dev server.',
        ttl: 7000,
      });
    }
    return;
  }
  const { provider, model } = picked;

  const bus = getBus();

  // Announce we're typing for the duration of the LLM call. Guaranteed pair —
  // the `false` emit runs in `finally`, so any thrown error still clears it.
  bus.emit({
    type: 'agent:typing',
    sessionId,
    visitorId,
    isTyping: true,
    author: 'ai',
    at: Date.now(),
  });

  try {
    const result = await chat(
      {
        messages: turns,
        opts: { provider, model, systemPrompt: VISITOR_CHAT_PROMPT, maxTokens: 512 },
      },
      transport,
    );
    const text = result.text.trim();
    if (!text) return;

    // Re-check ownership after the await — a human may have taken over while
    // we were waiting on the network.
    const stillAI = useConversationsStore.getState().byId.get(sessionId)?.ownedBy === 'ai';
    if (!stillAI) return;

    const messageId = newMessageId();

    bus.emit({
      type: 'agent:chat:message',
      sessionId,
      message: {
        messageId,
        sessionId,
        author: 'ai',
        authorId: visitorId,
        authorName: 'AI Assistant',
        text,
        sentAt: Date.now(),
      },
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'AI request failed';
    if (isLoggedIn()) {
      useNotificationsStore.getState().push({
        kind: 'system',
        title: 'AI assistant errored',
        body: msg.slice(0, 120),
        ttl: 6000,
      });
    }
  } finally {
    bus.emit({
      type: 'agent:typing',
      sessionId,
      visitorId,
      isTyping: false,
      author: 'ai',
      at: Date.now(),
    });
  }
}

/** Used by the conversation view's "Generate summary" button. */
export async function generateSummaryForConversation(
  sessionId: SessionId,
  turns: ChatTurn[],
): Promise<string> {
  const transport = transportFromEnv();
  const picked = pickBrowserProvider(transport);
  if (!picked) {
    throw new Error('No AI provider is configured (add a VITE_*_API_KEY).');
  }
  const { provider, model } = picked;
  const { SUMMARY_PROMPT } = await import('@prakash-software/shared');
  const result = await chat(
    {
      messages: turns,
      // Bumped from 240 → 2048. qwen3:14b is a reasoning model whose
      // `thinking` blocks eat the budget before the `text` summary lands.
      opts: { provider, model, systemPrompt: SUMMARY_PROMPT, maxTokens: 2048 },
    },
    transport,
  );
  return result.text.trim();
}

export function configuredProviders(): {
  anthropic: boolean;
  openai: boolean;
  openline: boolean;
} {
  const t = transportFromEnv();
  return {
    anthropic: !!t.anthropic,
    openai: !!t.openai,
    openline: !!t.openline,
  };
}
